import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Share, PlusSquare, X, Download } from "lucide-react";

const NAV_PATHS = ["/dashboard", "/wizard", "/chat", "/pricing", "/admin", "/payment", "/compliance"];

const DISMISS_KEY = "cl_install_dismissed_at";
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent) || window.matchMedia("(max-width: 767px)").matches;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const snoozed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < SNOOZE_MS;

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [evt, setEvt] = useState(() => window.__clInstallEvent || null);
  const { pathname } = useLocation();
  const hasNav = NAV_PATHS.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (!show) return;
    document.body.style.paddingBottom = "150px";
    return () => {
      document.body.style.paddingBottom = "";
    };
  }, [show]);

  useEffect(() => {
    if (isStandalone() || !isMobile() || snoozed()) return;
    const onReady = () => setEvt(window.__clInstallEvent);
    const onInstalled = () => setShow(false);
    window.addEventListener("cl-install-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    const timer = setTimeout(() => setShow(true), 2500);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("cl-install-ready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!show) return null;
  if (!evt && (pathname.startsWith("/home") || pathname.startsWith("/sign-in"))) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    evt.prompt();
    const { outcome } = await evt.userChoice;
    window.__clInstallEvent = null;
    setEvt(null);
    if (outcome === "accepted") setShow(false);
    else dismiss();
  };

  return (
    <div
      className="fixed inset-x-3 z-50 brutal-border bg-[#141414] shadow-2xl p-4"
      style={{ bottom: `calc(${hasNav ? 72 : 12}px + env(safe-area-inset-bottom))` }}
      data-testid="install-prompt"
    >
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="" className="w-12 h-12 shrink-0 border border-[#FFD600]/40" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-base">Add CarrierLaunch to your home screen</div>
          {evt ? (
            <p className="text-sm text-muted-foreground mt-1">Open it like an app, full screen, with one tap.</p>
          ) : isIOS() ? (
            <p className="text-sm text-muted-foreground mt-1" data-testid="install-ios-steps">
              Tap <Share size={14} className="inline -mt-0.5 text-[#0A84FF]" /> <b className="text-white">Share</b> in Safari, then{" "}
              <PlusSquare size={14} className="inline -mt-0.5" /> <b className="text-white">Add to Home Screen</b>.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-1" data-testid="install-menu-steps">
              Open your browser menu <b className="text-white">⋮</b> and tap <b className="text-white">Add to Home screen</b> or <b className="text-white">Install app</b>.
            </p>
          )}
        </div>
        <button onClick={dismiss} aria-label="Close" data-testid="install-dismiss" className="w-10 h-10 -mr-2 -mt-2 flex items-center justify-center text-muted-foreground hover:text-white">
          <X size={18} />
        </button>
      </div>
      {evt && (
        <button onClick={install} data-testid="install-button" className="mt-3 w-full min-h-[48px] cta-primary font-mono-label text-xs inline-flex items-center justify-center gap-2">
          <Download size={16} /> Add to Home Screen
        </button>
      )}
    </div>
  );
}
